import {Modal, Box, Text, Group, Kbd, Title, Flex} from '@mantine/core';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  TABLE_SHORTCUT_DESCRIPTIONS,
  EXPERIMENTAL_TABLE_SHORTCUT_DESCRIPTIONS,
  NOTEBOOK_SHORTCUTS,
  FORMATTING_SHORTCUTS,
  WORKSPACE_SHORTCUTS,
} from 'components/KeyboardShortcutDescriptions';
import {isNodeTesting} from 'lib/Browser';
import * as keyboard from 'util/Keyboard';

export const KEYBOARD_SHORTCUTS_MODAL_KEY = 'KEYBOARD_SHORTCUTS_MODAL';

interface KeyboardShortcutsModalProps {
  opened: boolean;
  onRequestClose: () => void;
}

function KeyboardShortcutsModalHeader(): JSX.Element {
  return (
    <Box p="sm">
      <Title order={4} mb="sm">
        Keyboard Shortcuts
      </Title>
      <Text fz="lg" color="dimmed" fw="normal">
        Browse all available keyboard shortcuts
      </Text>
    </Box>
  );
}

function ShortcutKeysGroup({
  normalizedShortcutText,
  skipOsTranslation,
}: {
  normalizedShortcutText: string;
  skipOsTranslation?: boolean;
}): JSX.Element {
  const displayShortcutText = skipOsTranslation
    ? normalizedShortcutText
    : keyboard.getOsSpecificShortcutText(normalizedShortcutText);
  const tokens = displayShortcutText.split(' + ');

  return (
    <Group spacing={4} noWrap={true}>
      {tokens.map((token, tokenIndex) => (
        <React.Fragment key={`${token}-${tokenIndex}`}>
          {tokenIndex > 0 && (
            <Text ml="xs" mr="xs" fz="xl" color="dimmed">
              +
            </Text>
          )}
          <Kbd size="xl" fz="xl" fw="normal" py="sm" px="md">
            {token}
          </Kbd>
        </React.Fragment>
      ))}
    </Group>
  );
}

function KeyboardShortcutRow({shortcut}: {shortcut: keyboard.ShortcutDescription}): JSX.Element {
  return (
    <Box component="li" py="sm" px="md" mb="sm" style={{listStyleType: 'none'}}>
      <Flex justify="space-between" align="center" gap="48px">
        <Text fz="xl" sx={{alignSelf: 'flex-start'}}>
          {shortcut.description}
        </Text>
        <Flex
          direction="column"
          gap="xs"
          align="flex-end"
          sx={{
            '& > *:not(:first-of-type)': {
              marginTop: '8px',
            },
          }}
        >
          {shortcut.shortcuts.map((shortcutItem, shortcutIndex) => (
            <Flex key={shortcutIndex} direction="row" gap="md" align="center">
              {shortcutIndex > 0 && (
                <Text fz="xl" color="dimmed">
                  or
                </Text>
              )}
              <ShortcutKeysGroup
                normalizedShortcutText={shortcutItem.normalizedShortcutText}
                skipOsTranslation={shortcutItem.skipOsTranslation}
              />
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
}

function KeyboardShortcutsModalSection({
  section,
  name,
  sectionId,
}: {
  section: Array<keyboard.ShortcutDescription>;
  name: string;
  sectionId: string;
}): JSX.Element {
  return (
    <Box component="section" aria-labelledby={sectionId}>
      <Title id={sectionId} order={4} mb="lg">
        {name}
      </Title>
      <Box component="ul" p="0" aria-label={`${name} shortcuts`}>
        {section.map((shortcut, index) => (
          <KeyboardShortcutRow key={index} shortcut={shortcut} />
        ))}
      </Box>
    </Box>
  );
}

function KeyboardShortcutsModal(props: KeyboardShortcutsModalProps): JSX.Element {
  const notebookShortcuts = [...NOTEBOOK_SHORTCUTS, ...FORMATTING_SHORTCUTS, ...WORKSPACE_SHORTCUTS];
  const tableShortcuts = BenchlingConfig.ELN_ENABLE_TABLE_EXPERIMENTAL_HOTKEYS
    ? [...TABLE_SHORTCUT_DESCRIPTIONS, ...EXPERIMENTAL_TABLE_SHORTCUT_DESCRIPTIONS]
    : TABLE_SHORTCUT_DESCRIPTIONS;

  return (
    <Modal
      size="sm"
      opened={props.opened}
      onClose={props.onRequestClose}
      closeOnClickOutside={true}
      closeOnEscape={true}
      closeButtonProps={{size: 'xs', style: {alignSelf: 'flex-start'}}}
      radius="lg"
      title={<KeyboardShortcutsModalHeader />}
      withinPortal={!isNodeTesting}
      zIndex={1040}
    >
      <Box
        p="sm"
        data-autofocus={true}
        sx={{
          '& > *:not(:first-of-type)': {
            marginTop: '36px',
          },
        }}
      >
        <KeyboardShortcutsModalSection section={notebookShortcuts} name="Notebook" sectionId="notebook" />
        <KeyboardShortcutsModalSection section={tableShortcuts} name="Table" sectionId="table" />
      </Box>
    </Modal>
  );
}

export default KeyboardShortcutsModal;
